import api from './api';

// ============================================================================
// TARIFFS
// ============================================================================

/**
 * Get all tariffs
 * @param {boolean} enabledOnly - Filter to only enabled tariffs
 */
export const getTariffs = async (enabledOnly = false) => {
  const response = await api.get('/superadmin/tariffs', {
    params: { enabled_only: enabledOnly }
  });
  return response.data;
};

/**
 * Create a new tariff
 */
export const createTariff = async (tariffData) => {
  const response = await api.post('/superadmin/tariffs', tariffData);
  return response.data;
};

/**
 * Update an existing tariff
 */
export const updateTariff = async (tariffHash, tariffData) => {
  const response = await api.put(`/superadmin/tariffs/${tariffHash}`, tariffData);
  return response.data;
};

/**
 * Assign tariff to an account
 * @param {number} accountId - Account ID
 * @param {number|null} tariffId - Tariff ID (null to remove tariff)
 */
export const assignAccountTariff = async (accountId, tariffId) => {
  const response = await api.put(`/superadmin/accounts/${accountId}/tariff`, {
    tariff_id: tariffId
  });
  return response.data;
};

// ============================================================================
// GLOBAL CONSUMPTION
// ============================================================================

/**
 * Get global consumption data (all accounts)
 */
export const getGlobalConsumption = async (startDate, endDate, skip = 0, limit = 100) => {
  const response = await api.get('/superadmin/consumption', {
    params: { start_date: startDate, end_date: endDate, skip, limit }
  });
  return response.data;
};

/**
 * Get consumption breakdown by LLM model
 */
export const getConsumptionByModel = async (startDate, endDate) => {
  const response = await api.get('/superadmin/consumption/by-model', {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

/**
 * Get consumption breakdown by operation type
 */
export const getConsumptionByOperation = async (startDate, endDate) => {
  const response = await api.get('/superadmin/consumption/by-operation', {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

// ============================================================================
// ACCOUNT CONSUMPTION
// ============================================================================

/**
 * Get consumption records for a specific account
 * @param {number} accountId - Account ID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string|null} measureType - Filter by measure type (ANALYSIS or OPERATION)
 * @param {number} skip - Pagination offset
 * @param {number} limit - Pagination limit
 */
export const getAccountConsumption = async (accountId, startDate, endDate, measureType = null, skip = 0, limit = 20) => {
  const params = { start_date: startDate, end_date: endDate, skip, limit };
  if (measureType) params.measure_type = measureType;

  const response = await api.get(`/superadmin/accounts/${accountId}/consumption`, { params });
  return response.data;
};

/**
 * Get consumption summary for an account
 */
export const getAccountConsumptionSummary = async (accountId, startDate, endDate) => {
  const response = await api.get(`/superadmin/accounts/${accountId}/consumption/summary`, {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

/**
 * Get billing info for an account
 */
export const getAccountBilling = async (accountId, startDate, endDate) => {
  const response = await api.get(`/superadmin/accounts/${accountId}/consumption/billing`, {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

// ============================================================================
// MY ACCOUNT CONSUMPTION (for account admins)
// ============================================================================

/**
 * Get consumption records for my account (account admin only)
 */
export const getMyConsumption = async (startDate, endDate, measureType = null, skip = 0, limit = 20) => {
  const params = { start_date: startDate, end_date: endDate, skip, limit };
  if (measureType) params.measure_type = measureType;

  const response = await api.get('/accounts/me/consumption', { params });
  return response.data;
};

/**
 * Get consumption summary for my account
 */
export const getMyConsumptionSummary = async (startDate, endDate) => {
  const response = await api.get('/accounts/me/consumption/summary', {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

/**
 * Get consumption by model for my account
 */
export const getMyConsumptionByModel = async (startDate, endDate) => {
  const response = await api.get('/accounts/me/consumption/by-model', {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

/**
 * Get consumption by operation for my account
 */
export const getMyConsumptionByOperation = async (startDate, endDate) => {
  const response = await api.get('/accounts/me/consumption/by-operation', {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

/**
 * Get billing info for my account
 */
export const getMyBilling = async (startDate, endDate) => {
  const response = await api.get('/accounts/me/consumption/billing', {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};
